import { AllFreeformAnswers, AnswerSource} from "../../redux/survey/survey.types";
import { ReactElement, useMemo, useState } from "react";
import { freeformListForFilter} from "../../pages/view-survey/view-survey.helpers";
import ExpandableTitle from "./expandable-title";
import TextBubble from "./text-bubble";
import FeedbackEmptyState from "./feedback-empty-state.component";

interface FreeformResponseListProps {
  readonly allResponses: AllFreeformAnswers;
  readonly sourceFilter: AnswerSource;
  readonly useRedesign?: boolean;
}

const FreeformResponseList = ({ allResponses, sourceFilter, useRedesign = false }: FreeformResponseListProps) => {
  const [isExpanded, setIsExpanded] = useState(!useRedesign);
  const responses = useMemo(() => {
    return freeformListForFilter(allResponses, sourceFilter).slice(0, isExpanded ? undefined : 2);
  }, [allResponses, sourceFilter, isExpanded]);

  const onExpansionClick = () => {
    setIsExpanded(!isExpanded);
  };

  const renderExpansionButton = () => {
    if (!useRedesign) {
      return null;
    }

    return (
      <div
        onClick={onExpansionClick}
        className='flex justify-center border-t border-t-gray-light-10 mt-6 py-6 cursor-pointer'
      >
        {isExpanded ? 'Collapse' : 'Expand'}
      </div>
    );
  };

  const renderResponseList = (): ReactElement => {
    return (
      <div className='flex flex-col'>
        <ul className="flex flex-col gap-2 px-6">
          {responses.map(({answer, createdAt, userType}, index) => (
            <TextBubble
              feedback={answer}
              createdAt={createdAt}
              userType={userType}
              key={`${userType}-${index}`}
              useRedesign={useRedesign}
            />
          ))}
        </ul>
        {renderExpansionButton()}
      </div>
    );
  };

  const renderEmptyState = (): ReactElement => {
    if (!useRedesign) {
      return (
        <div className="flex flex-row justify-center px-4 pb-4 text-gray-501 text-sm">
          No freeform responses for selected respondent type.
        </div>
      );
    }

    return <FeedbackEmptyState />;
  };

  const renderContent = () => {
    if (responses.length) {
      return renderResponseList();
    } else {
      return renderEmptyState();
    }
  };

  return (
    <>
      { useRedesign ? (renderContent()) : (
        <ExpandableTitle title={'Freeform Responses'}>
          {renderContent()}
        </ExpandableTitle>
      )
      }
    </>
  );
};

export default FreeformResponseList;
